/**
 * BuyerManagementTab
 *
 * Buyer master-data CRUD. Extracted from the retired `TechOps → Buyers`
 * tab (Feb 2026 relocation) and dropped into `Demand → Buyer Management`.
 * The visual + interaction contract is identical to the previous
 * TechOps tab — nothing was rebuilt, only re-homed:
 *   - list table, column set, and detail-link column identical
 *   - create/edit dialog identical
 *   - two-mode Import dialog (Buyers + Addresses) identical
 *   - delete confirmation flow identical
 *   - CSV export of flattened addresses identical
 *
 * What did change: every write action is now gated with `<PermButton>`
 * on the new (real, enforced) `Buyer:{CREATE,UPDATE,DELETE}` grants.
 * TECH_OPS_ENGINEER was dropped from CREATE/UPDATE, DEMAND_PLANNER
 * added to CREATE/UPDATE/DELETE. Read stays wildcard.
 *
 * State is fully local — no dependency on TechOps.js's shared
 * `deleteConfirm` / `editingItem` / `showImportDialog` slots. That
 * keeps this component drop-in-usable from any page.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import PermButton from "@/components/PermButton";
import { Plus, Upload, Download, Pencil, Trash2, Tag, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ZohoCustomerLinkDialog from "@/components/ZohoCustomerLinkDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { uploadWithReceipt } from "@/lib/bulkUploadReceipt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = { name: "", gst: "", email: "", phone_no: "", poc_name: "" };

export const BuyerManagementTab = () => {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [showImport, setShowImport] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Feb 2026 — inline Zoho link dialog. `linkTarget` holds the buyer
  // being linked; null means the dialog is closed.
  const [linkTarget, setLinkTarget] = useState(null);
  // Sep 2026 — KAM assignment moved to the Customer Detail page as the
  // sole entry point; the list keeps only the read-only KAM column and
  // no longer opens the dialog (shared `KamAssignDialog`).

  const buyerFileRef = useRef(null);
  const addressFileRef = useRef(null);

  const fetchBuyers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/buyers`);
      setBuyers((res.data || []).filter((b) => b.status === "ACTIVE"));
    } catch {
      toast.error("Failed to load buyers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBuyers();
  }, [fetchBuyers]);

  // ── Create / Edit ────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/buyers/${editing.id}`, form);
        toast.success("Buyer updated");
      } else {
        await axios.post(`${API}/buyers`, form);
        toast.success("Buyer created with auto-generated code");
      }
      setShowDialog(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      fetchBuyers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save buyer");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowDialog(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || "",
      gst: b.gst || "",
      email: b.email || "",
      phone_no: b.phone_no || "",
      poc_name: b.poc_name || "",
    });
    setShowDialog(true);
  };

  // ── Delete ───────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/buyers/${id}`);
      toast.success("Buyer deleted");
      fetchBuyers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete buyer");
    }
    setDeleteConfirm(null);
  };

  // ── Bulk import (Buyers) ─────────────────────────────────────────
  const handleBuyerImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const data = await uploadWithReceipt(`${API}/buyers/bulk-import`, formData, {
        axiosConfig: { headers: { "Content-Type": "multipart/form-data" } },
        showToast: false,
      });
      if (data.duplicates && data.duplicates.length > 0) {
        const n = data.duplicates.length;
        const list = data.duplicates.slice(0, 5).map((d) => d.name).join(", ");
        toast.error(
          `${data.created} created, ${n} skipped (duplicates). No overwrites allowed.\nDuplicates: ${list}${n > 5 ? "..." : ""} — Receipt downloaded.`,
          { duration: 8000 },
        );
      } else {
        toast.success((data.message || "Import complete") + " — Receipt downloaded.");
      }
      setShowImport(false);
      fetchBuyers();
    } catch (err) {
      const d = err.response?.data;
      if (d?.duplicates && d.duplicates.length > 0) {
        const n = d.duplicates.length;
        const list = d.duplicates.slice(0, 5).map((x) => x.name).join(", ");
        toast.error(
          `Upload blocked: ${n} duplicate(s) found. No overwrites allowed.\nDuplicates: ${list}${n > 5 ? "..." : ""}`,
          { duration: 8000 },
        );
      } else {
        toast.error(d?.detail || d?.message || "Import failed");
      }
    } finally {
      setImportLoading(false);
      if (buyerFileRef.current) buyerFileRef.current.value = "";
    }
  };

  // ── Bulk import (Addresses) — same endpoint semantics as TechOps ─
  const handleAddressImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const data = await uploadWithReceipt(`${API}/buyers/addresses/bulk-import`, formData, {
        axiosConfig: { headers: { "Content-Type": "multipart/form-data" } },
        showToast: false,
      });
      toast.success(`${data.message || "Address import complete"} — Receipt downloaded.`, {
        duration: 6000,
      });
      setShowImport(false);
      fetchBuyers();
    } catch (err) {
      const d = err.response?.data;
      toast.error(d?.detail || d?.message || err.message || "Address import failed");
    } finally {
      setImportLoading(false);
      if (addressFileRef.current) addressFileRef.current.value = "";
    }
  };

  // ── CSV export of flattened addresses ────────────────────────────
  const handleDownloadAddresses = () => {
    try {
      const rows = [[
        "Customer Code", "Customer Name", "Address Type", "Address Label",
        "GSTIN", "Contact Name", "Contact Phone", "Address Line 1", "Address Line 2",
        "City", "State", "Pincode", "Country", "Is Default", "Status",
      ]];
      buyers.forEach((b) => {
        (b.addresses || []).forEach((a) => {
          rows.push([
            b.customer_code || "", b.name || "",
            a.address_type || "", a.label || "",
            a.gstin || "", a.contact_name || "", a.contact_phone || "",
            a.line1 || "", a.line2 || "",
            a.city || "", a.state || "", a.pincode || "",
            a.country || "India",
            a.is_default ? "Y" : "N",
            a.status || "ACTIVE",
          ]);
        });
      });
      const csv = rows
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `buyer-addresses-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${rows.length - 1} address row(s)`);
    } catch (err) {
      toast.error(`Download failed: ${err.message}`);
    }
  };

  // ── CSV export of buyers list ────────────────────────────────────
  const handleDownloadBuyers = () => {
    setDownloading(true);
    try {
      const rows = [[
        "Customer Code", "Customer Name", "GST", "Email", "Phone No", "POC Name",
      ]];
      buyers.forEach((b) => {
        rows.push([
          b.customer_code || b.code || "",
          b.name || "",
          b.gst || "",
          b.email || b.contact_email || "",
          b.phone_no || "",
          b.poc_name || "",
        ]);
      });
      const csv = rows
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `buyers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${buyers.length} buyer(s)`);
    } catch (err) {
      toast.error(`Download failed: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="buyer-management-tab">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-bold">Buyer Management</h2>
          <p className="text-xs text-zinc-500 mt-1 font-mono">
            Master data — customers, contacts, and Bill-To / Ship-To addresses.
            Read-only sales / billed-SKU views live elsewhere.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadBuyers}
            disabled={downloading || buyers.length === 0}
            data-testid="download-buyers-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {downloading ? "..." : `Download (${buyers.length})`}
          </Button>
          <PermButton
            entity="Buyer"
            action="CREATE"
            variant="outline"
            onClick={() => setShowImport(true)}
            className="uppercase text-xs tracking-wide"
            data-testid="import-buyers-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Excel
          </PermButton>
          <PermButton
            entity="Buyer"
            action="CREATE"
            onClick={openCreate}
            className="uppercase text-xs tracking-wide"
            data-testid="add-buyer-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Buyer
          </PermButton>
        </div>
      </div>

      <div className="border rounded-sm overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-50">
            <tr>
              <th className="h-10 px-4 text-left font-mono text-xs uppercase">Customer Code</th>
              <th className="h-10 px-4 text-left font-mono text-xs uppercase">Customer Name</th>
              <th className="h-10 px-4 text-left font-mono text-xs uppercase">GST</th>
              <th className="h-10 px-4 text-left font-mono text-xs uppercase">Email</th>
              <th className="h-10 px-4 text-left font-mono text-xs uppercase">Phone No</th>
              <th className="h-10 px-4 text-left font-mono text-xs uppercase">POC Name</th>
              <th className="h-10 px-4 text-left font-mono text-xs uppercase">KAM</th>
              <th className="h-10 px-4 text-center font-mono text-xs uppercase">Zoho</th>
              <th className="h-10 px-4 text-center font-mono text-xs uppercase">Bill-To</th>
              <th className="h-10 px-4 text-center font-mono text-xs uppercase">Ship-To</th>
              <th className="h-10 px-4 text-left font-mono text-xs uppercase">Brands</th>
              <th className="h-10 px-4 text-right font-mono text-xs uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && buyers.length === 0 && (
              <tr>
                <td colSpan={12} className="p-8 text-center text-muted-foreground">Loading…</td>
              </tr>
            )}
            {buyers.map((b) => {
              const addrs = (b.addresses || []).filter((a) => (a.status || "ACTIVE") === "ACTIVE");
              const billCount = addrs.filter((a) => a.address_type === "BILL_TO").length;
              const shipCount = addrs.filter((a) => a.address_type === "SHIP_TO").length;
              const isLinked = !!b.customer_zoho_id;
              return (
                <tr key={b.id} className="border-t">
                  <td className="p-4 font-mono font-bold">{b.customer_code || b.code || "-"}</td>
                  <td className="p-4">
                    <RouterLink
                      to={`/customers/${b.id}`}
                      className="text-blue-700 hover:underline font-medium"
                      data-testid={`buyer-detail-link-${b.customer_code || b.code}`}
                    >
                      {b.name}
                    </RouterLink>
                  </td>
                  <td className="p-4 text-sm font-mono">{b.gst || "-"}</td>
                  <td className="p-4 text-sm">{b.email || b.contact_email || "-"}</td>
                  <td className="p-4 text-sm font-mono">{b.phone_no || "-"}</td>
                  <td className="p-4 text-sm">{b.poc_name || "-"}</td>
                  <td className="p-4 text-sm" data-testid={`buyer-kam-${b.id}`}>
                    {b.current_kam ? (
                      <>
                        <div className="font-medium">{b.current_kam.name}</div>
                        <div className="text-xs text-muted-foreground">{b.current_kam.email}</div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-4 text-center" data-testid={`buyer-zoho-status-${b.id}`}>
                    {isLinked ? (
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 font-mono">Linked</Badge>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <Badge variant="destructive" className="font-mono">Not linked</Badge>
                        <PermButton
                          entity="Buyer" action="UPDATE"
                          variant="link" size="sm"
                          className="h-auto p-0 text-xs text-blue-700"
                          onClick={() => setLinkTarget(b)}
                          data-testid={`buyer-zoho-link-btn-${b.id}`}
                        >
                          <Link2 className="w-3 h-3 mr-1" /> Link to Zoho
                        </PermButton>
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-center" data-testid={`buyer-bill-count-${b.customer_code || b.code}`}>
                    <Badge variant={billCount > 0 ? "secondary" : "outline"} className="font-mono">{billCount}</Badge>
                  </td>
                  <td className="p-4 text-center" data-testid={`buyer-ship-count-${b.customer_code || b.code}`}>
                    <Badge variant={shipCount > 0 ? "secondary" : "outline"} className="font-mono">{shipCount}</Badge>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {(b.brands_dispatched || []).length > 0 ? (
                        b.brands_dispatched.slice(0, 3).map((brand, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            <Tag className="w-3 h-3 mr-1" />
                            {brand}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                      {(b.brands_dispatched || []).length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{b.brands_dispatched.length - 3}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <PermButton
                      entity="Buyer"
                      action="UPDATE"
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(b)}
                      data-testid={`edit-buyer-${b.customer_code || b.code}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </PermButton>
                    <PermButton
                      entity="Buyer"
                      action="DELETE"
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm({ id: b.id, name: b.name })}
                      data-testid={`delete-buyer-${b.customer_code || b.code}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </PermButton>
                  </td>
                </tr>
              );
            })}
            {!loading && buyers.length === 0 && (
              <tr>
                <td colSpan={12} className="p-8 text-center text-muted-foreground">
                  No buyers defined
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog
        open={showDialog}
        onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setEditing(null);
            setForm(EMPTY_FORM);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Buyer" : "Create Buyer"}</DialogTitle>
            <DialogDescription>
              {editing
                ? `Editing buyer: ${editing.customer_code || editing.code}`
                : "Customer code will be auto-generated (e.g., CUST001)"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {editing && (
              <div>
                <Label>Customer Code</Label>
                <Input
                  value={editing.customer_code || editing.code || ""}
                  disabled
                  className="font-mono bg-zinc-100"
                />
                <p className="text-xs text-muted-foreground mt-1">Auto-generated, cannot be changed</p>
              </div>
            )}
            <div>
              <Label>Customer Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Enter customer name"
              />
            </div>
            <div>
              <Label>GST Number</Label>
              <Input
                value={form.gst}
                onChange={(e) => setForm({ ...form, gst: e.target.value.toUpperCase() })}
                placeholder="e.g., 22AAAAA0000A1Z5"
                className="font-mono uppercase"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                type="email"
                placeholder="contact@company.com"
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                value={form.phone_no}
                onChange={(e) => setForm({ ...form, phone_no: e.target.value })}
                placeholder="+91 98765 43210"
              />
            </div>
            <div>
              <Label>POC Name (Point of Contact)</Label>
              <Input
                value={form.poc_name}
                onChange={(e) => setForm({ ...form, poc_name: e.target.value })}
                placeholder="Contact person name"
              />
            </div>
            <PermButton
              entity="Buyer"
              action={editing ? "UPDATE" : "CREATE"}
              onClick={handleSave}
              className="w-full"
              disabled={!form.name.trim()}
              data-testid="save-buyer-btn"
            >
              {editing ? "Update" : "Create Buyer"}
            </PermButton>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog (Buyers + Addresses in one) */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Buyers or Addresses</DialogTitle>
            <DialogDescription>
              Buyers import creates new customers. Address import tags Bill-To /
              Ship-To addresses onto existing customers (matched by Customer Code).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <div className="text-sm font-semibold mb-2">1. Import Buyers</div>
              <input
                ref={buyerFileRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleBuyerImport}
                className="hidden"
                id="buyer-file-input-demand"
              />
              <label htmlFor="buyer-file-input-demand" className="cursor-pointer">
                <Button variant="outline" disabled={importLoading} asChild>
                  <span>{importLoading ? "Importing..." : "Select Buyers Excel"}</span>
                </Button>
              </label>
              <p className="text-xs text-muted-foreground mt-2">
                Cols: Customer Name (req), GST, Email, Phone No, POC Name
              </p>
            </div>

            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <div className="text-sm font-semibold mb-2">2. Import Addresses</div>
              <input
                ref={addressFileRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleAddressImport}
                className="hidden"
                id="address-file-input-demand"
              />
              <label htmlFor="address-file-input-demand" className="cursor-pointer">
                <Button variant="outline" disabled={importLoading} asChild>
                  <span data-testid="import-addresses-btn">
                    {importLoading ? "Importing..." : "Select Addresses Excel"}
                  </span>
                </Button>
              </label>
              <p className="text-xs text-muted-foreground mt-2">
                Cols: Customer Code, Address Type (BILL_TO/SHIP_TO), Address Label, GSTIN,
                Contact Name, Contact Phone, Address Line 1/2, City, State (req), Pincode,
                Country, Is Default
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">
                Upsert on (Customer Code + Address Type + Address Label). Unknown customers rejected per-row.
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownloadAddresses}
                className="mt-2"
                data-testid="download-addresses-btn"
              >
                <Download className="w-3 h-3 mr-1" /> Download all addresses
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete buyer?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteConfirm?.name}&quot;? This action
              cannot be undone if there are no dependencies.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteConfirm?.id && handleDelete(deleteConfirm.id)}
              data-testid="confirm-delete-buyer-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Feb 2026 — inline Zoho customer search-and-pick dialog. */}
      <ZohoCustomerLinkDialog
        open={!!linkTarget}
        onOpenChange={(o) => !o && setLinkTarget(null)}
        buyer={linkTarget}
        onLinked={() => fetchBuyers()}
        testIdPrefix="buyer-mgmt-zoho-link"
      />
    </div>
  );
};

export default BuyerManagementTab;